import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useBackoffice } from '../backoffice/useBackoffice';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

export type KycVerification = {
  kyc_link: string;
  kyc_status: 'approved' | 'pending' | 'rejected';
  kyc_sub_status: string;
  type: 'general' | 'bridge';
};

export type CustomerRow = {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  beneficiary_type: 'individual' | 'business';
  company_name?: string;
  created_at: string;
  address?: {
    country?: string;
    country_alpha_2?: string;
    city?: string;
    address_line_1?: string;
    postal_code?: string;
    state?: string;
  };
  identifying_information?: {
    dob?: string;
    id_document?: {
      number?: string;
      issuing_country?: string;
      type?: string;
    };
    tax_number?: string;
    tax_number_type?: string;
  };
  money_movement_settings?: {
    core_api?: {
      enabled_corridors: string[];
    };
    fees?: {
      offramp?: {
        percentage: number;
      };
      cross_chain_transfer?: {
        percentage: number;
      };
      onramp?: {
        percentage: number;
      };
    };
    implementer_contexts?: {
      bridge?: {
        context: {
          customerId: string;
        };
      };
    };
  };
  kyc_verifications: KycVerification[];
  kycs: {
    kyc_flow_link: string;
    status: 'approved' | 'pending' | 'rejected';
    status_breakdown: {
      currency: string;
      payment_rails: string;
      status: 'approved' | 'pending';
    }[];
    sub_status:
      | 'kyc_form_submission_started'
      | 'kyc_form_submission_accepted'
      | 'kyc_form_resubmission_required'
      | null;
  };
};

export const useCustomers = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { developerId } = useBackoffice();
  const [searchParams] = useSearchParams();
  const pageSize = searchParams.get('pageSize') || '10';
  const pageNumber = searchParams.get('pageNumber') || '1';
  const searchString = searchParams.get('searchString') || '';

  const customersQuery = useQuery({
    queryKey: ['customers', developerId, pageSize, pageNumber, searchString],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/customers`, {
        params: {
          search_string: searchString,
          consumerId: developerId,
          limit: parseInt(pageSize),
          offset: parseInt(pageSize) * (parseInt(pageNumber) - 1),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data?.data;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 2,
  });

  return customersQuery;
};
